/**
 * threadNav — pure math for the message-step navigation bubbles
 * (2026-08-12, thread-scrolling UX round).
 *
 * The bubbles walk the transcript one message at a time: ↑ lands on the
 * previous message's top, ↓ on the next one's, and stepping past the last
 * message hands off to the existing jump-to-latest scroll. All positions are
 * CONTENT-space tops (anchor top relative to the scroll container's content,
 * i.e. `rect.top - containerRect.top + scrollTop`), so the functions stay
 * independent of live DOM and testable.
 *
 * Progress guarantee: a step must actually move the viewport. Each landing
 * sits NAV_PADDING above the target bubble, so the candidate filter compares
 * against the LANDED position (top - NAV_PADDING) with an EPSILON slack —
 * without that, clicking ↑/↓ twice in a row re-selects the bubble you are
 * already parked on and the thread never moves.
 */

/** Breathing room left above a bubble when the viewport lands on it. */
export const THREAD_NAV_PADDING_PX = 12;

/** Slack that keeps re-clicks from re-selecting the current landing spot. */
export const THREAD_NAV_EPSILON_PX = 4;

/** Distance from an absolute edge within which the edge counts as reached. */
export const THREAD_EDGE_EPSILON_PX = 8;

/**
 * Scroll target for "previous message": the nearest anchor whose landing
 * spot is strictly above the current scrollTop. Above the first anchor the
 * step degrades to the absolute top, so ↑ always makes upward progress
 * until the edge. `anchorTops` must be ascending content-space tops.
 */
export function pickPreviousMessageScrollTop(
  anchorTops: readonly number[],
  scrollTop: number,
): number {
  for (let i = anchorTops.length - 1; i >= 0; i -= 1) {
    const landing = Math.max(0, anchorTops[i] - THREAD_NAV_PADDING_PX);
    if (landing < scrollTop - THREAD_NAV_EPSILON_PX) {
      return landing;
    }
  }
  return 0;
}

/**
 * Scroll target for "next message": the first anchor whose landing spot is
 * strictly below the current scrollTop, or `null` when no anchor remains
 * below — the caller should jump to the latest message instead.
 */
export function pickNextMessageScrollTop(
  anchorTops: readonly number[],
  scrollTop: number,
): number | null {
  for (let i = 0; i < anchorTops.length; i += 1) {
    const landing = Math.max(0, anchorTops[i] - THREAD_NAV_PADDING_PX);
    if (landing > scrollTop + THREAD_NAV_EPSILON_PX) {
      return landing;
    }
  }
  return null;
}

/** Is the viewport parked at the very top of the transcript? */
export function isAtThreadTopEdge(scrollTop: number): boolean {
  return scrollTop <= THREAD_EDGE_EPSILON_PX;
}

/** Is the viewport parked at the very bottom of the transcript? */
export function isAtThreadBottomEdge(
  scrollTop: number,
  scrollHeight: number,
  clientHeight: number,
): boolean {
  return scrollHeight - scrollTop - clientHeight <= THREAD_EDGE_EPSILON_PX;
}

/**
 * Content-space top of an anchor element inside the scroll container —
 * the one place the rect math lives so every caller measures identically.
 */
export function anchorContentTop(
  anchorRectTop: number,
  containerRectTop: number,
  scrollTop: number,
): number {
  return anchorRectTop - containerRectTop + scrollTop;
}

/**
 * 1-based index of the message a stepper landing corresponds to — the "12" in
 * a "12 / 40" readout.
 *
 * Landings are `anchorTop - NAV_PADDING` (clamped at 0), so several anchors
 * can share a landing at the very top of the transcript; the nearest one wins,
 * which is the message the reader actually sees at the top of the viewport.
 */
export function landingIndex(
  anchorTops: readonly number[],
  landedScrollTop: number,
): number {
  let best = 0;
  let bestDistance = Infinity;
  for (let i = 0; i < anchorTops.length; i += 1) {
    const landing = Math.max(0, anchorTops[i] - THREAD_NAV_PADDING_PX);
    const distance = Math.abs(landing - landedScrollTop);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = i;
    }
  }
  return best + 1;
}
